// ScheduleInviteSender.tsx
import { SendCANmail, SendAdHocCANmail } from "api/canmail/CANmailAPI";
import type { AdHocInviteRecipient } from "./MeetingInviteSender";

type InviteAttachments = Array<{ name: string; type: string; data: string }> | null;

const MEET_FROM = "meet@streamdata.com";
const SCHEDULE_SUBJECT = "You Have Been Invited to a CANmeet Meeting";

/**
 * Builds the scheduled-meeting invite email body and its ICS calendar attachment.
 * Shared by the saved-list and ad-hoc senders — only the delivery call differs.
 */
const buildScheduleInviteEmail = (
  meetingName: string,
  url: string,
  meetingDate: string, // format "YYYY-MM-DD"
  startTime: string, // format "HH:mm" in 24-hour format
  duration: number,
  recurrenceParam: string,
  attachments: InviteAttachments = []
): { emailContents: string; allAttachments: InviteAttachments } => {
  // Calculate a friendly duration label:
  const durationLabel =
    duration < 60
      ? `${duration} minutes`
      : duration / 60 === 1
      ? "1 hour"
      : `${duration / 60} hours`;

  // User's local input represents their local time
  const startDateTime = new Date(`${meetingDate}T${startTime}`);
  const endDateTime = new Date(startDateTime.getTime() + duration * 60000);

  // Get sender's timezone info
  const senderTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const senderTimezoneName = new Intl.DateTimeFormat('en-US', {
    timeZone: senderTimezone,
    timeZoneName: 'long'
  }).formatToParts(startDateTime).find(part => part.type === 'timeZoneName')?.value || senderTimezone;

  const senderTimezoneShort = new Intl.DateTimeFormat('en-US', {
    timeZone: senderTimezone,
    timeZoneName: 'short'
  }).formatToParts(startDateTime).find(part => part.type === 'timeZoneName')?.value || '';

  // Format in sender's local timezone
  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: senderTimezone
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: senderTimezone
  };

  const senderDateString = startDateTime.toLocaleDateString('en-US', dateOptions);
  const senderTimeString = startDateTime.toLocaleTimeString('en-US', timeOptions);

  // Build recurrence HTML if the recurrence parameter is set (i.e. not "none").
  const recurrenceHTML =
    recurrenceParam !== "none"
      ? `<div style="margin-top: 10px; font-size: 16px; color: #555;">
           This meeting will repeat <strong>${recurrenceParam}</strong>.
         </div>`
      : "";

  const emailContents = `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="x-apple-disable-message-reformatting">
    <title>Meeting Invitation</title>
    <style type="text/css">
      /* Base Styles */
      body {
        margin: 0;
        padding: 0;
        -webkit-text-size-adjust: 100%;
        background-color: #e8f3fc;
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
        color: #333;
      }
      table {
        border-collapse: collapse;
      }
      a {
        color: #1a82e2;
        text-decoration: none;
      }
      /* Responsive Layout */
      @media only screen and (min-width: 520px) {
        .container {
          width: 500px !important;
        }
      }
      @media only screen and (max-width: 520px) {
        .container {
          width: 100% !important;
          padding: 0 10px;
        }
      }
    </style>
  </head>
  <body>
    <table width="100%" bgcolor="#e8f3fc" cellpadding="0" cellspacing="0">
      <tr>
        <td align="center">
          <!-- Container -->
          <table class="container" cellpadding="0" cellspacing="0" style="background-color: #ffffff; margin: 20px 0; border-radius: 8px; overflow: hidden; box-shadow: 0px 2px 6px rgba(0,0,0,0.15);">
            <!-- Header -->
            <tr>
              <td align="center" style="padding: 20px; background-color: #64b5f6;">
                <h1 style="margin: 0; font-size: 24px; font-weight: 400; color: #ffffff;">You Have Been Invited to a CANmeet Meeting</h1>
              </td>
            </tr>
            <!-- Meeting Details -->
            <tr>
              <td align="center" style="padding: 20px;">
                <h2 style="margin-bottom: 10px; font-size: 18px; font-weight: 400;">Meeting Name: ${meetingName}</h2>
                <div style="font-size: 16px; line-height: 1.6;">
                  <div style="margin-bottom: 8px;"><strong>Date:</strong> ${senderDateString}</div>
                  <div style="margin-bottom: 8px;"><strong>Time:</strong> ${senderTimeString} ${senderTimezoneShort}</div>
                  <div style="margin-bottom: 8px;"><strong>Duration:</strong> ${durationLabel}</div>
                  ${recurrenceHTML}
                </div>
                <div style="margin-top: 15px; padding: 12px; background-color: #e3f2fd; border-left: 4px solid #64b5f6; border-radius: 4px; font-size: 14px; color: #333; line-height: 1.6;">
                  <p style="margin: 0 0 8px 0;"><strong>Timezone Information:</strong></p>
                  <p style="margin: 0 0 8px 0;">This meeting is scheduled for <strong>${senderTimeString} ${senderTimezoneShort}</strong> (${senderTimezoneName}).</p>
                  <p style="margin: 0;">Recipients in different timezones will automatically see the meeting at the corresponding time in their timezone when they add the <strong>invite.ics</strong> calendar file to their calendar (Outlook, Google Calendar, etc.).</p>
                </div>
              </td>
            </tr>
            <!-- Join Button -->
            <tr>
              <td align="center" style="padding: 20px;">
                <a href="${url}" target="_blank" style="display: inline-block; background-color: #64b5f6; color: #ffffff; padding: 12px 24px; border-radius: 4px; font-size: 16px; font-weight: 600;">
                  Click To Join
                </a>
              </td>
            </tr>
            <!-- Alternative Link -->
            <tr>
              <td align="center" style="padding: 10px 20px 20px; font-size: 14px; color: #666;">
                <p style="margin: 0;">Or copy and paste the following link into your browser:</p>
                <p style="margin: 5px 0 0;">
                  <a href="${url}" target="_blank" style="word-break: break-all;">${url}</a>
                </p>
              </td>
            </tr>
          </table>
          <!-- End Container -->
        </td>
      </tr>
    </table>
  </body>
</html>`;

  // Format dates for ICS in UTC
  const formatDateForICSUTC = (date: Date): string => {
    const pad = (num: number) => String(num).padStart(2, "0");
    return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
      date.getUTCDate()
    )}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
      date.getUTCSeconds()
    )}Z`;
  };

  const dtStartICS = formatDateForICSUTC(startDateTime);
  const dtEndICS = formatDateForICSUTC(endDateTime);
  const dtStamp = formatDateForICSUTC(new Date());
  const randomUID = Math.floor(1000 + Math.random() * 9000);

  const recurrenceRule = recurrenceParam !== "none" 
    ? `RRULE:FREQ=${recurrenceParam.toUpperCase()}`
    : "";

  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Calendar Invite//EN
CALSCALE:GREGORIAN
METHOD:REQUEST
BEGIN:VEVENT
UID:${dtStartICS}-${randomUID}-meet@streamdata.com
DTSTAMP:${dtStamp}
DTSTART:${dtStartICS}
DTEND:${dtEndICS}
SUMMARY:${meetingName}
ORGANIZER;CN=CANmeet:mailto:meet@streamdata.com
DESCRIPTION:Join meeting: ${url}
STATUS:CONFIRMED
${recurrenceRule ? recurrenceRule + "\n" : ""}
SEQUENCE:0
BEGIN:VALARM
TRIGGER:-PT15M
ACTION:DISPLAY
DESCRIPTION:Reminder
END:VALARM
END:VEVENT
END:VCALENDAR `;

  const icsBase64 = btoa(icsContent);

  const icsAttachment = {
    name: "invite.ics",
    type: "text/calendar",
    data: icsBase64,
  };

  const allAttachments = [icsAttachment, ...(attachments || [])];

  return { emailContents, allAttachments };
};

/** Send a scheduled-meeting invite (with ICS attachment) to a saved mail list. */
export const sendScheduleInvite = async (
  listId: string,
  meetingName: string,
  url: string,
  meetingDate: string,
  startTime: string,
  duration: number,
  recurrenceParam: string,
  attachments: InviteAttachments = []
) => {
  const id = parseInt(listId, 10);
  const { emailContents, allAttachments } = buildScheduleInviteEmail(
    meetingName,
    url,
    meetingDate,
    startTime,
    duration,
    recurrenceParam,
    attachments
  );

  // Send scheduled meeting invite email with ICS calendar attachment
  return await SendCANmail(
    id, // list_id (number)
    MEET_FROM, // from
    MEET_FROM, // reply_to
    SCHEDULE_SUBJECT, // subject
    emailContents, // email_contents
    allAttachments, // attachments (includes ICS file)
    null // to (null for list emails)
  );
};

/**
 * Send a scheduled-meeting invite (with ICS attachment) to an ad-hoc recipient set
 * (no saved list). Used when the organiser builds the invitee list in the meeting creator.
 */
export const sendScheduleInviteAdHoc = async (
  recipients: AdHocInviteRecipient[],
  meetingName: string,
  url: string,
  meetingDate: string,
  startTime: string,
  duration: number,
  recurrenceParam: string,
  attachments: InviteAttachments = []
) => {
  const { emailContents, allAttachments } = buildScheduleInviteEmail(
    meetingName,
    url,
    meetingDate,
    startTime,
    duration,
    recurrenceParam,
    attachments
  );

  return await SendAdHocCANmail(
    recipients,
    MEET_FROM, // from
    MEET_FROM, // reply_to
    SCHEDULE_SUBJECT, // subject
    emailContents, // email_contents
    allAttachments // attachments (includes ICS file)
  );
};
